import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, dateiLaden, fehlertext } from "./api";
import { alsStunden } from "./datum";
import { euro } from "./kosten";
import { Fehler, Karte, Knopf, Laedt, Textknopf } from "./ui";

/**
 * Was am Kunden vereinbart ist, und was dort gearbeitet wurde.
 *
 * Bis hierher liess sich die Arbeitszeit nur nach Mitarbeiter auswerten. Das
 * ist die Sicht der Lohnabrechnung. Die Sicht des Geschaefts fehlte, und
 * genau sie steht am Anfang jeder Diskussion mit einem Eigentuemer: sechzehn
 * Stunden Garten vereinbart, vierzehneinhalb geleistet.
 *
 * **Das Soll ist anteilig.** Eine Wochenzeit mal vier waere jeden Monat zu
 * niedrig, im Jahr um fast einen ganzen Monat Leistung. Gerechnet wird
 * deshalb nach Tagen, und der Monat mit einunddreissig Tagen hat mehr Soll
 * als der mit achtundzwanzig.
 *
 * **Das Ist kommt aus den Einsaetzen**, also aus der Zeit vor Ort, nicht aus
 * dem Arbeitstag. Wer neun Stunden Jornada hat und davon zwei bei diesem
 * Kunden war, steht hier mit zwei Stunden.
 *
 * Ein Klick auf eine Zeile oeffnet das Einzelblatt mit den Einsaetzen. Erst
 * damit ist die Zahl belegt und nicht nur behauptet.
 *
 * **Kosten stehen daneben, nicht darin.** Stunden und Euro werden nicht
 * verrechnet, denn ein Stundensatz steht nirgends im System. Zwei ehrliche
 * Spalten sagen mehr als eine Zahl aus einem geratenen Satz.
 */

interface Kundenzeile {
  kundeId: string;
  kunde: string;
  soll: number;
  ist: number;
  differenz: number;
  objekte: number;
  einsaetze: number;
  materialCent: number;
  fremdCent: number;
  kostenCent: number;
  belege: number;
}

interface Uebersicht {
  monat: string;
  kunden: Kundenzeile[];
  summe: { soll: number; ist: number; kostenCent: number };
}

interface Detailzeile {
  objektId: string;
  objekt: string;
  abteilungId: string | null;
  abteilung: string | null;
  soll: number;
  ist: number;
  differenz: number;
  materialCent: number;
  fremdCent: number;
  kostenCent: number;
}

interface Beleg {
  id: string;
  objekt: string;
  mitarbeiter: string;
  tag: string;
  art: string;
  bezeichnung: string;
  lieferant: string | null;
  menge: number | null;
  einheit: string | null;
  betragCent: number;
  weiterberechenbar: boolean;
  abteilung: string | null;
}

interface Einsatz {
  id: string;
  objekt: string;
  mitarbeiter: string;
  tag: string;
  von: string;
  bis: string;
  minuten: number;
  abteilung: string | null;
  geplant: boolean;
}

interface Einzelblatt {
  kunde: { id: string; name: string };
  zeilen: Detailzeile[];
  einsaetze: Einsatz[];
  belege: Beleg[];
  summe: { soll: number; ist: number; materialCent: number; fremdCent: number; kostenCent: number };
}

export function Kundenauswertung({ monat }: { monat: string }) {
  const { t } = useTranslation();
  const [daten, setDaten] = useState<Uebersicht | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtDatei, setLaedtDatei] = useState(false);

  useEffect(() => {
    setDaten(null);
    setOffen(null);
    void api.get<Uebersicht>(`/auswertung/kunden?monat=${monat}`)
      .then(setDaten)
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [monat, t]);

  /*
   * Der Knopf steht hier und nicht oben im Seitenkopf.
   *
   * Oben liegen die Dateien der Mitarbeitersicht, also Auszug und Register.
   * Was heruntergeladen wird, soll neben dem stehen, was man gerade sieht,
   * sonst laedt jemand die Abrechnung herunter und wundert sich, warum darin
   * keine Mitarbeiter stehen.
   */
  async function exportieren() {
    setLaedtDatei(true);
    setFehler(null);
    try {
      await dateiLaden(`/auswertung/kunden/export?monat=${monat}`, `Abrechnung-${monat}.xlsx`);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.laden")));
    } finally {
      setLaedtDatei(false);
    }
  }

  if (fehler) return <Fehler text={fehler} />;
  if (!daten) return <Laedt />;
  if (daten.kunden.length === 0) {
    return <Karte><p className="text-sm text-leise">{t("auswertung.leer")}</p></Karte>;
  }

  return (
    <div className="mt-3">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Knopf art="neben" disabled={laedtDatei} onClick={() => void exportieren()}>
          {t("auswertung.export")}
        </Knopf>
        <p className="text-xs text-leise">{t("auswertung.exportHinweis")}</p>
      </div>
      <div className="overflow-x-auto rounded-card bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-linie text-xs text-leise">
            <tr>
              <th className="p-3 text-start">{t("auswertung.kunde")}</th>
              <th className="p-3 text-end">{t("auswertung.soll")}</th>
              <th className="p-3 text-end">{t("auswertung.ist")}</th>
              <th className="p-3 text-end">{t("auswertung.differenz")}</th>
              <th className="p-3 text-end">{t("auswertung.einsaetze")}</th>
              <th className="p-3 text-end">{t("auswertung.kosten")}</th>
            </tr>
          </thead>
          <tbody>
            {daten.kunden.map((k) => (
              <Kundenblock
                key={k.kundeId}
                zeile={k}
                monat={monat}
                offen={offen === k.kundeId}
                onWechsel={() => setOffen(offen === k.kundeId ? null : k.kundeId)}
              />
            ))}
          </tbody>
          <tfoot className="border-t border-linie text-sm font-bold">
            <tr>
              <td className="p-3">{t("auswertung.summe")}</td>
              <td className="p-3 text-end tabular-nums">{alsStunden(daten.summe.soll)}</td>
              <td className="p-3 text-end tabular-nums">{alsStunden(daten.summe.ist)}</td>
              <td className="p-3 text-end tabular-nums">
                <Abweichung minuten={daten.summe.ist - daten.summe.soll} />
              </td>
              <td className="p-3" />
              <td className="p-3 text-end tabular-nums">{euro(daten.summe.kostenCent)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function Kundenblock({
  zeile, monat, offen, onWechsel
}: {
  zeile: Kundenzeile;
  monat: string;
  offen: boolean;
  onWechsel: () => void;
}) {
  const { t } = useTranslation();
  const [blatt, setBlatt] = useState<Einzelblatt | null>(null);

  useEffect(() => {
    if (!offen) return;
    setBlatt(null);
    void api.get<Einzelblatt>(`/auswertung/kunden/${zeile.kundeId}?monat=${monat}`)
      .then(setBlatt)
      .catch(() => setBlatt(null));
  }, [offen, zeile.kundeId, monat]);

  return (
    <>
      <tr className="border-b border-linie/60">
        <td className="p-3">
          <Textknopf onClick={onWechsel}>{zeile.kunde}</Textknopf>
          {/*
            * Stunden ohne hinterlegte Sollzeit sind kein Anzeigefehler,
            * sondern ein Fund: entweder fehlt die Vereinbarung im System oder
            * es wurde unbezahlt gearbeitet.
            */}
          {zeile.soll === 0 && zeile.ist > 0 && (
            <span className="block text-xs text-verweis">{t("auswertung.ohneSoll")}</span>
          )}
        </td>
        <td className="p-3 text-end tabular-nums">{alsStunden(zeile.soll)}</td>
        <td className="p-3 text-end tabular-nums">{alsStunden(zeile.ist)}</td>
        <td className="p-3 text-end tabular-nums">
          <Abweichung minuten={zeile.differenz} />
        </td>
        <td className="p-3 text-end tabular-nums text-leise">{zeile.einsaetze}</td>
        <td className="p-3 text-end tabular-nums">
          {zeile.kostenCent === 0
            ? <span className="text-leise">—</span>
            : euro(zeile.kostenCent)}
        </td>
      </tr>
      {offen && (
        <tr className="border-b border-linie">
          <td colSpan={6} className="bg-grund/30 p-3">
            {!blatt ? <Laedt /> : (
              <>
                <table className="w-full text-sm">
                  <tbody>
                    {blatt.zeilen.map((z) => (
                      <tr key={`${z.objektId}-${z.abteilungId ?? "-"}`}>
                        <td className="py-1">
                          {z.objekt}
                          <span className="text-leise">
                            {" · "}{z.abteilung ?? t("plan.ohneAbteilung")}
                          </span>
                        </td>
                        <td className="py-1 text-end tabular-nums">{alsStunden(z.soll)}</td>
                        <td className="py-1 text-end tabular-nums">{alsStunden(z.ist)}</td>
                        <td className="py-1 text-end tabular-nums">
                          <Abweichung minuten={z.differenz} />
                        </td>
                        <td className="py-1 text-end tabular-nums">
                          {z.kostenCent === 0
                            ? <span className="text-leise">—</span>
                            : euro(z.kostenCent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/*
                  * Material und Fremdleistung getrennt, denn die beiden Zahlen
                  * fuehren zu verschiedenen Gespraechen: die eine mit dem
                  * Lieferanten, die andere mit dem Eigentuemer.
                  */}
                {blatt.summe.kostenCent > 0 && (
                  <p className="mt-2 text-sm text-leise">
                    {t("auswertung.material")}: {euro(blatt.summe.materialCent)}
                    {" · "}
                    {t("auswertung.fremd")}: {euro(blatt.summe.fremdCent)}
                  </p>
                )}

                <h3 className="mt-4 mb-1 text-xs font-bold text-leise">
                  {t("auswertung.einsaetze")}
                </h3>
                {blatt.einsaetze.length === 0 ? (
                  <p className="text-sm text-leise">{t("auswertung.keineEinsaetze")}</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {blatt.einsaetze.map((e) => (
                      <li key={e.id} className="tabular-nums">
                        {e.tag} {e.von} bis {e.bis}
                        <span className="text-leise">
                          {" · "}{e.mitarbeiter}
                          {" · "}{e.objekt}
                          {e.abteilung ? ` · ${e.abteilung}` : ""}
                          {" · "}{alsStunden(e.minuten)}
                          {!e.geplant && ` · ${t("auswertung.ungeplant")}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {blatt.belege.length > 0 && (
                  <>
                    <h3 className="mt-4 mb-1 text-xs font-bold text-leise">
                      {t("auswertung.belege")}
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {blatt.belege.map((b) => (
                        <li key={b.id} className="tabular-nums">
                          {b.tag} {euro(b.betragCent)}
                          <span className="text-leise">
                            {" · "}{b.bezeichnung}
                            {" · "}{t(b.art === "material" ? "kosten.material" : "kosten.fremd")}
                            {" · "}{b.objekt}
                            {b.abteilung ? ` · ${b.abteilung}` : ""}
                            {b.menge !== null ? ` · ${b.menge} ${b.einheit ?? ""}`.trimEnd() : ""}
                            {b.lieferant ? ` · ${b.lieferant}` : ""}
                            {!b.weiterberechenbar && ` · ${t("kosten.nichtWeiter")}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Die Abweichung mit Vorzeichen.
 *
 * Minus heisst: es wurde weniger geleistet als vereinbart. Ohne das
 * ausdrueckliche Plus liest sich die Gegenrichtung beim Ueberfliegen wie ein
 * Rueckstand, obwohl sie das Gegenteil ist.
 */
function Abweichung({ minuten }: { minuten: number }) {
  if (minuten === 0) return <span className="text-leise">—</span>;
  return (
    <span className={minuten < 0 ? "font-semibold text-red-700" : "font-semibold text-verweis"}>
      {minuten < 0 ? `-${alsStunden(-minuten)}` : `+${alsStunden(minuten)}`}
    </span>
  );
}
